import {
    isValidRentalPeriod,
    type QuoteDraft,
    type QuoteLine,
    type QuoteRequirement,
    type QuoteUnit,
} from './quote-builder-logic'
import type {
    CreateQuoteInput,
    Quote,
    QuoteLineItem,
    QuoteRequirementWriteInput,
    QuoteStatus,
    UpdateQuoteInput,
} from './schemas'

const LINE_ITEM_LOCKED_STATUSES = new Set<QuoteStatus>(['accepted', 'rejected', 'voided'])

export function createEmptyQuoteDraft(): QuoteDraft {
    return {
        meta: {
            name: '',
            customerId: null,
            customerContactId: null,
            poNumber: '',
            terms: '',
            dueDate: null,
            rentalPeriod: null,
        },
        status: 'draft',
        lines: [],
        requirements: [],
    }
}

function lineItemToEditorLine(lineItem: QuoteLineItem): QuoteLine {
    if (lineItem.kind === 'percent') {
        return {
            kind: 'percent',
            id: lineItem.id,
            description: lineItem.description,
            rate: lineItem.rate,
            chargeType: lineItem.chargeType,
        }
    }

    return {
        kind: 'item',
        id: lineItem.id,
        description: lineItem.description,
        quantity: lineItem.quantity ?? 0,
        duration: lineItem.duration,
        unit: (lineItem.unit ?? 'ea') as QuoteUnit,
        rate: lineItem.rate,
        chargeType: lineItem.chargeType,
        forRentalPeriod: lineItem.forRentalPeriod,
    }
}

export function quoteToDraft(quote: Quote): QuoteDraft {
    return {
        meta: {
            name: quote.name ?? '',
            customerId: quote.customerId,
            customerContactId: quote.customerContact?.id ?? null,
            poNumber: quote.poNumber ?? '',
            terms: quote.terms ?? '',
            dueDate: quote.dueDate,
            rentalPeriod: quote.rentalPeriod,
        },
        status: quote.status,
        lines: quote.lineItems.map(lineItemToEditorLine),
        requirements: quote.requirements.map((requirement) =>
            requirement.kind === 'worker'
                ? {
                      id: requirement.id,
                      lineId: requirement.quoteLineItemId,
                      kind: 'worker' as const,
                      workerTypeId: requirement.workerTypeId ?? '',
                      qty: requirement.qty,
                  }
                : {
                      id: requirement.id,
                      lineId: requirement.quoteLineItemId,
                      kind: 'equipment' as const,
                      rentalAssetTypeId: requirement.rentalAssetTypeId ?? '',
                      qty: requirement.qty,
                  },
        ),
    }
}

function editorLineToWriteInput(line: QuoteLine) {
    if (line.kind === 'percent') {
        return {
            kind: 'percent' as const,
            description: line.description,
            rate: line.rate,
            chargeType: line.chargeType,
        }
    }

    return {
        kind: 'item' as const,
        description: line.description,
        rate: line.rate,
        quantity: line.quantity,
        duration: line.duration,
        unit: line.unit,
        chargeType: line.chargeType,
        forRentalPeriod: line.forRentalPeriod ?? false,
    }
}

function rentalPeriodToSave(meta: QuoteDraft['meta']) {
    const period = meta.rentalPeriod
    return period != null && isValidRentalPeriod(period) ? period : null
}

function draftMetaFields(meta: QuoteDraft['meta']) {
    return {
        name: meta.name,
        customerContactId: meta.customerContactId,
        poNumber: meta.poNumber,
        terms: meta.terms,
        dueDate: meta.dueDate,
    }
}

export function draftToCreateInput(draft: QuoteDraft): CreateQuoteInput {
    if (draft.meta.customerId == null) {
        throw new Error('Customer is required to create a quote')
    }

    return {
        customerId: draft.meta.customerId,
        ...draftMetaFields(draft.meta),
        lineItems: draft.lines.map(editorLineToWriteInput),
        rentalPeriod: rentalPeriodToSave(draft.meta),
    }
}

export function draftToUpdateInput(draft: QuoteDraft): UpdateQuoteInput {
    return {
        ...draftMetaFields(draft.meta),
        status: draft.status,
        ...(LINE_ITEM_LOCKED_STATUSES.has(draft.status)
            ? {}
            : {
                  lineItems: draft.lines.map(editorLineToWriteInput),
                  rentalPeriod: rentalPeriodToSave(draft.meta),
              }),
    }
}

function buildSavedQuoteShape(draft: QuoteDraft) {
    const cents = (value: number) => Math.round(value * 100)
    return {
        ...draftMetaFields(draft.meta),
        lineItems: draft.lines.map((line) => {
            const input = editorLineToWriteInput(line)
            return {
                ...input,
                rate: cents(input.rate),
                ...(input.kind === 'item' ? {quantity: cents(input.quantity)} : {}),
            }
        }),
        rentalPeriod: rentalPeriodToSave(draft.meta),
    }
}

export function hasUnsavedChanges(draft: QuoteDraft, saved: Quote): boolean {
    return (
        JSON.stringify(buildSavedQuoteShape(draft)) !==
        JSON.stringify(buildSavedQuoteShape(quoteToDraft(saved)))
    )
}

function requirementTypeId(requirement: QuoteRequirement): string | null {
    if (requirement.kind === 'worker') {
        return requirement.workerTypeId.length > 0 ? requirement.workerTypeId : null
    }

    return requirement.rentalAssetTypeId.length > 0 ? requirement.rentalAssetTypeId : null
}

function requirementAggregationKey(requirement: QuoteRequirement, typeId: string): string {
    return `${requirement.lineId}:${requirement.kind}:${typeId}`
}

export function draftRequirementsToWriteInput(
    requirements: QuoteRequirement[],
): QuoteRequirementWriteInput[] {
    const aggregated = new Map<string, QuoteRequirementWriteInput>()

    for (const requirement of requirements) {
        const typeId = requirementTypeId(requirement)
        if (typeId == null) {
            continue
        }

        const key = requirementAggregationKey(requirement, typeId)
        const existing = aggregated.get(key)

        if (existing == null) {
            aggregated.set(
                key,
                requirement.kind === 'worker'
                    ? {
                          quoteLineItemId: requirement.lineId,
                          kind: 'worker',
                          qty: requirement.qty,
                          workerTypeId: typeId,
                      }
                    : {
                          quoteLineItemId: requirement.lineId,
                          kind: 'equipment',
                          qty: requirement.qty,
                          rentalAssetTypeId: typeId,
                      },
            )
            continue
        }

        aggregated.set(key, {...existing, qty: existing.qty + requirement.qty})
    }

    return [...aggregated.values()]
}
